import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Home, HandHeart, TrendingUp, Printer } from "lucide-react";
import { usePrintProcessSteps } from "@/hooks/usePrintProcessSteps";

const ProcessSteps = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const { printProcessSteps } = usePrintProcessSteps();

  const steps = [
    {
      icon: <Home className="h-16 w-16 text-blue-600" />,
      title: "Step 1: Original Contract",
      description: "A property seller needs to sell but offers financing to attract more buyers",
      example: "John sells his $200,000 house to Mary with a land contract: $40,000 down, $160,000 financed at 7% for 15 years",
      details: [
        "Monthly payment: $1,438",
        "John becomes the note holder",
        "Mary gets homeownership",
        "Win-win for both parties"
      ]
    },
    {
      icon: <HandHeart className="h-16 w-16 text-yellow-600" />,
      title: "Step 2: Life Happens",
      description: "The note holder needs immediate cash and decides to sell their note",
      example: "John needs $120,000 for a business opportunity and can't wait 15 years for the note to pay off",
      details: [
        "Medical emergency funds needed",
        "Business investment opportunity",
        "Retirement cash needs",
        "Estate settlement requirements"
      ]
    },
    {
      icon: <TrendingUp className="h-16 w-16 text-green-600" />,
      title: "Step 3: Smart Investment",
      description: "An investor buys the note at a discount, creating higher yields for both parties",
      example: "You buy John's $160,000 note for $135,000, creating a 12.8% yield while John gets immediate cash",
      details: [
        "You receive $1,438/month",
        "12.8% annual return on $135,000",
        "Secured by real estate",
        "John gets $135,000 immediately"
      ]
    }
  ];

  const nextStep = () => {
    setCurrentStep((prev) => (prev + 1) % steps.length);
  };

  const prevStep = () => {
    setCurrentStep((prev) => (prev - 1 + steps.length) % steps.length);
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Step-by-Step Process
              </h2>
              <p className="text-xl text-gray-600">
                Click through each step to understand how note investing works
              </p>
            </div>
            <Button
              onClick={printProcessSteps}
              variant="outline"
              className="flex items-center gap-2 ml-4"
            >
              <Printer className="h-4 w-4" />
              Print Process
            </Button>
          </div>
        </div>

        <div className="flex justify-center mb-8">
          <div className="flex space-x-2">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentStep === index ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        <Card className="max-w-4xl mx-auto">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                {steps[currentStep].icon}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {steps[currentStep].title}
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {steps[currentStep].description}
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold text-blue-900 mb-2">Real Example:</h4>
              <p className="text-blue-800">{steps[currentStep].example}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {steps[currentStep].details.map((detail, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">{detail}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center">
              <Button
                onClick={prevStep}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              <span className="text-gray-500">
                {currentStep + 1} of {steps.length}
              </span>
              
              <Button
                onClick={nextStep}
                className="flex items-center gap-2"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ProcessSteps;